/**
 * CriterionRuleEditor — the facts-engine compiled-predicate editor for one
 * criterion (docs/crm-eligibility-facts-plan.md §7; rule vocabulary = the
 * prescreen rule engine, `evalCriterionRule` in backend crmPivot.js §RULE
 * SEMANTICS, mirrored here for DISPLAY/EDITING only — evaluation stays
 * server-side). Modeled on forms/EligibilityRuleBuilder.tsx's interaction
 * patterns (colored boxes, operator selects, inline value widgets) adapted
 * for the richer fact-typed vocabulary.
 *
 * DRAFT-ONLY: only rendered editable when the criterion's set.status ===
 * 'draft' (enforced by the caller, ReviewCriteriaPanel). Confirmed sets get
 * <CriterionRuleSummary> instead — a compact read-only chip line.
 *
 * Structured builder covers: a single leaf, or an and/or group of leaves.
 * Nested groups-within-groups are supported for DISPLAY (recursive) but the
 * "Add condition" affordance only appends leaves to the top-level group — an
 * admin who needs deeper nesting drops into "Edit as JSON". The raw-JSON
 * toggle exposes `rule` + `rule_facts` together as one editable JSON object
 * so cross-field edits (e.g. adding a rule_facts entry a new leaf needs) stay
 * atomic; it parses lazily (validate-on-blur), never fighting the structured
 * view for keystrokes.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Plus, X } from 'lucide-react';
import {
  CriterionFactType,
  CriterionRule,
  CriterionRuleFact,
  CriterionRuleGroup,
  CriterionRuleLeaf,
  StudyReviewCriterion,
} from './criteriaTypes';

// ---------------------------------------------------------------------------
// Vocabulary (mirrors RULE SEMANTICS — display/editing only; the backend is
// the source of truth for what actually validates on PATCH).
// ---------------------------------------------------------------------------

const FACT_TYPES: { value: CriterionFactType; label: string }[] = [
  { value: 'lab_result', label: 'Lab result' },
  { value: 'vital', label: 'Vital (incl. BMI/weight/height)' },
  { value: 'condition', label: 'Condition' },
  { value: 'medication', label: 'Medication' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'family_history', label: 'Family history' },
  { value: 'social_history', label: 'Social history' },
  { value: 'demographic', label: 'Demographic' },
  { value: 'test_event', label: 'Test event (order/collection only)' },
  { value: 'other', label: 'Other' },
];

const FACT_TYPE_LABEL = new Map(FACT_TYPES.map((f) => [f.value, f.label]));
const factTypeLabel = (t: string): string => FACT_TYPE_LABEL.get(t as CriterionFactType) || t;

/** Default recency window shown as a placeholder when null (§13.4). */
const DEFAULT_WINDOW_HINT: Partial<Record<CriterionFactType, string>> = {
  lab_result: 'default 365d',
  vital: 'default 180d',
};

const OP_OPTIONS: { value: CriterionRuleLeaf['op']; label: string }[] = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
  { value: 'gt', label: 'is greater than' },
  { value: 'gte', label: 'is greater than or equal to' },
  { value: 'lt', label: 'is less than' },
  { value: 'lte', label: 'is less than or equal to' },
  { value: 'between', label: 'is between' },
  { value: 'in', label: 'is one of' },
  { value: 'exists', label: 'exists (any usable fact in-window)' },
];

const OP_LABEL = new Map(OP_OPTIONS.map((o) => [o.value, o.label]));

const isGroup = (r: CriterionRule | null | undefined): r is CriterionRuleGroup =>
  !!r && typeof r === 'object' && 'combinator' in r && Array.isArray((r as CriterionRuleGroup).conditions);

const isLeaf = (r: CriterionRule | null | undefined): r is CriterionRuleLeaf =>
  !!r && typeof r === 'object' && 'op' in r && !('combinator' in r);

const emptyLeaf = (): CriterionRuleLeaf => ({ op: 'eq', value: '' });

const defaultLeafValue = (op: CriterionRuleLeaf['op']): Partial<CriterionRuleLeaf> => {
  if (op === 'between') return { min: undefined, max: undefined, value: undefined, values: undefined };
  if (op === 'in') return { values: [], value: undefined, min: undefined, max: undefined };
  if (op === 'exists') return { value: undefined, min: undefined, max: undefined, values: undefined };
  return { value: '', min: undefined, max: undefined, values: undefined };
};

// ---------------------------------------------------------------------------
// Shared input styling (mirrors EligibilityRuleBuilder's primary-accent inputs)
// ---------------------------------------------------------------------------

const inputCls =
  'block w-full min-h-[40px] rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const labelCls = 'text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500';

// ---------------------------------------------------------------------------
// rule_facts editor — the small table of "which fact keys does this rule
// read" entries. Most rules have exactly one; `fact` on a leaf is only
// required to disambiguate when there are 2+.
// ---------------------------------------------------------------------------

function RuleFactsEditor({ facts, onChange }: {
  facts: CriterionRuleFact[];
  onChange: (facts: CriterionRuleFact[]) => void;
}) {
  const update = (i: number, patch: Partial<CriterionRuleFact>) => {
    const next = facts.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const add = () => onChange([...facts, { fact_type: 'lab_result', key: '', window_days: null, unit: null }]);
  const remove = (i: number) => onChange(facts.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-2.5 dark:border-gray-700 dark:bg-gray-900/30">
      <div className="mb-1.5 flex items-center justify-between">
        <span className={labelCls}>Fact keys this rule reads</span>
        <button
          type="button"
          onClick={add}
          className="inline-flex min-h-[32px] items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
        >
          <Plus className="h-3.5 w-3.5" /> Add fact
        </button>
      </div>
      {facts.length === 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          No fact keys yet — add at least one (e.g. type <span className="font-mono">lab_result</span>, key{' '}
          <span className="font-mono">tsh</span>).
        </p>
      )}
      <div className="space-y-2">
        {facts.map((f, i) => (
          <div key={i} className="flex flex-wrap items-end gap-1.5 rounded-md bg-white p-2 dark:bg-gray-800">
            <div className="min-w-[9rem] flex-1">
              <span className={labelCls}>Type</span>
              <select
                value={f.fact_type}
                onChange={(e) => update(i, { fact_type: e.target.value as CriterionFactType })}
                className={inputCls}
              >
                {FACT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="min-w-[8rem] flex-1">
              <span className={labelCls}>Key</span>
              <input
                type="text"
                value={f.key}
                onChange={(e) => update(i, { key: e.target.value.slice(0, 200) })}
                placeholder="e.g. tsh, condition.thyroid_cancer, bmi"
                className={`${inputCls} font-mono`}
              />
            </div>
            <div className="w-24">
              <span className={labelCls} title="Blank = fact-type default (labs 365d, vitals 180d)">
                Window (days)
              </span>
              <input
                type="number"
                min={1}
                value={f.window_days ?? ''}
                onChange={(e) => update(i, { window_days: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder={DEFAULT_WINDOW_HINT[f.fact_type] || 'no window'}
                className={inputCls}
              />
            </div>
            <div className="w-24">
              <span className={labelCls}>Unit</span>
              <input
                type="text"
                value={f.unit ?? ''}
                onChange={(e) => update(i, { unit: e.target.value.trim() ? e.target.value.slice(0, 40) : null })}
                placeholder="e.g. kg, mIU/L"
                className={inputCls}
              />
            </div>
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label={`Remove fact key ${i + 1}`}
              className="mb-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One leaf row (op + value widget). `factOptions` = the keys already defined
// in rule_facts, for the `fact` disambiguator select.
// ---------------------------------------------------------------------------

function LeafRow({ leaf, factOptions, onChange, onRemove }: {
  leaf: CriterionRuleLeaf;
  factOptions: string[];
  onChange: (leaf: CriterionRuleLeaf) => void;
  onRemove?: () => void;
}) {
  const setOp = (op: CriterionRuleLeaf['op']) => onChange({ ...leaf, op, ...defaultLeafValue(op) });

  const renderValue = () => {
    switch (leaf.op) {
      case 'between':
        return (
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              value={leaf.min ?? ''}
              onChange={(e) => onChange({ ...leaf, min: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="Min"
              className={inputCls}
            />
            <span className="shrink-0 text-xs text-gray-400">and</span>
            <input
              type="number"
              value={leaf.max ?? ''}
              onChange={(e) => onChange({ ...leaf, max: e.target.value === '' ? undefined : Number(e.target.value) })}
              placeholder="Max"
              className={inputCls}
            />
          </div>
        );
      case 'in':
        return (
          <input
            type="text"
            value={Array.isArray(leaf.values) ? leaf.values.join(', ') : ''}
            onChange={(e) => onChange({
              ...leaf,
              values: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
            })}
            placeholder="active, historical, resolved"
            className={`${inputCls} font-mono`}
          />
        );
      case 'exists':
        return <p className="py-1.5 text-xs italic text-gray-400 dark:text-gray-500">No value — passes on any usable in-window fact.</p>;
      default:
        return (
          <input
            type="text"
            value={leaf.value == null ? '' : String(leaf.value)}
            onChange={(e) => onChange({ ...leaf, value: e.target.value })}
            placeholder="Value (number or text)"
            className={inputCls}
          />
        );
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-1.5 rounded-md border border-primary-100 bg-primary-50/40 p-2 dark:border-primary-900/40 dark:bg-primary-900/10">
      {factOptions.length > 1 && (
        <div className="w-40">
          <span className={labelCls}>Fact</span>
          <select
            value={leaf.fact || ''}
            onChange={(e) => onChange({ ...leaf, fact: e.target.value || undefined })}
            className={`${inputCls} font-mono`}
          >
            <option value="">(ambiguous — pick one)</option>
            {factOptions.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
      )}
      <div className="w-48">
        <span className={labelCls}>Operator</span>
        <select value={leaf.op} onChange={(e) => setOp(e.target.value as CriterionRuleLeaf['op'])} className={inputCls}>
          {OP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="min-w-[10rem] flex-1">
        <span className={labelCls}>Value</span>
        {renderValue()}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove condition"
          className="mb-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Structured rule editor: single leaf <-> and/or group of leaves.
// ---------------------------------------------------------------------------

function StructuredRuleBuilder({ rule, factOptions, onChange }: {
  rule: CriterionRule | null;
  factOptions: string[];
  onChange: (rule: CriterionRule | null) => void;
}) {
  const group = isGroup(rule) ? rule : null;
  const leaf = isLeaf(rule) ? rule : null;

  if (!rule) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-400 dark:text-gray-500">No rule set — this criterion stays an AI judgment call.</span>
        <button
          type="button"
          onClick={() => onChange(emptyLeaf())}
          className="inline-flex min-h-[36px] items-center gap-1 rounded-lg border border-primary-200 px-2.5 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-400 dark:hover:bg-primary-900/20"
        >
          <Plus className="h-3.5 w-3.5" /> Add a rule
        </button>
      </div>
    );
  }

  // Group not expressible by leaves the builder understands (nested groups
  // inside conditions) — still show it, but each nested group renders as a
  // read-only summary line with a note to use raw JSON to edit deeper.
  const conditionsAreAllLeaves = group ? group.conditions.every((c) => isLeaf(c)) : true;

  return (
    <div className="space-y-2">
      {leaf && (
        <LeafRow
          leaf={leaf}
          factOptions={factOptions}
          onChange={(l) => onChange(l)}
          onRemove={() => onChange(null)}
        />
      )}
      {group && (
        <div className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-700">
              {(['and', 'or'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onChange({ ...group, combinator: c })}
                  className={`min-h-[32px] rounded-md px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors duration-150 ${
                    group.combinator === c
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-xs font-medium text-gray-400 transition-colors duration-150 hover:text-red-600 dark:hover:text-red-400"
            >
              Remove rule
            </button>
          </div>
          {!conditionsAreAllLeaves && (
            <div className="mb-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              This group has a nested sub-group — the structured builder shows top-level conditions only.
              Use &ldquo;Edit as JSON&rdquo; below to change nested structure.
            </div>
          )}
          <div className="space-y-1.5">
            {group.conditions.map((c, i) => (
              isLeaf(c) ? (
                <LeafRow
                  key={i}
                  leaf={c}
                  factOptions={factOptions}
                  onChange={(l) => {
                    const next = group.conditions.slice();
                    next[i] = l;
                    onChange({ ...group, conditions: next });
                  }}
                  onRemove={() => onChange({ ...group, conditions: group.conditions.filter((_, idx) => idx !== i) })}
                />
              ) : (
                <div key={i} className="rounded-md border border-dashed border-gray-300 p-2 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
                  Nested {(c as CriterionRuleGroup).combinator?.toUpperCase()} group ({(c as CriterionRuleGroup).conditions?.length || 0} conditions) — edit as JSON.
                </div>
              )
            ))}
          </div>
          <button
            type="button"
            onClick={() => onChange({ ...group, conditions: [...group.conditions, emptyLeaf()] })}
            className="mt-2 inline-flex min-h-[32px] items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
          >
            <Plus className="h-3.5 w-3.5" /> Add condition
          </button>
        </div>
      )}
      {leaf && (
        <button
          type="button"
          onClick={() => onChange({ combinator: 'and', conditions: [leaf, emptyLeaf()] })}
          className="inline-flex min-h-[32px] items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-gray-200"
        >
          <Plus className="h-3.5 w-3.5" /> Turn into an AND/OR group
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Qualifiers chip input (enter to add; mirrors the 'in' pill pattern).
// ---------------------------------------------------------------------------

function QualifiersInput({ qualifiers, onChange }: {
  qualifiers: string[];
  onChange: (q: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const v = draft.trim().slice(0, 80);
    if (!v || qualifiers.length >= 20 || qualifiers.includes(v)) { setDraft(''); return; }
    onChange([...qualifiers, v]);
    setDraft('');
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {qualifiers.map((q, i) => (
          <span
            key={`${i}:${q}`}
            className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
          >
            {q}
            <button
              type="button"
              onClick={() => onChange(qualifiers.filter((_, idx) => idx !== i))}
              aria-label={`Remove qualifier ${q}`}
              className="rounded-full p-0.5 hover:bg-purple-200 dark:hover:bg-purple-800/50"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        onBlur={commit}
        disabled={qualifiers.length >= 20}
        placeholder={qualifiers.length >= 20 ? 'Max 20 qualifiers' : "Type a qualifier from the criterion text, press Enter (e.g. 'active', 'unstable')"}
        className={`${inputCls} mt-1.5 disabled:opacity-50`}
      />
      <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
        Non-empty qualifiers force at least an AI look — Tier 0 only auto-resolves when a fact&rsquo;s
        captured qualifiers decide it cleanly.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Raw-JSON fallback — rule + rule_facts together, validate-on-blur.
// ---------------------------------------------------------------------------

interface RawJson {
  rule: CriterionRule | null;
  rule_facts: CriterionRuleFact[];
}

function RawJsonEditor({ value, onApply }: {
  value: RawJson;
  onApply: (v: RawJson) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);

  // Re-sync the textarea when the structured builder changes the value out
  // from under us (only while there's no in-progress edit error to lose).
  useEffect(() => {
    if (!error) setText(JSON.stringify(value, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const validate = (): RawJson | null => {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && ('rule' in parsed || 'rule_facts' in parsed)) {
        const rule = 'rule' in parsed ? parsed.rule : value.rule;
        const rule_facts = Array.isArray(parsed.rule_facts) ? parsed.rule_facts : value.rule_facts;
        setError(null);
        return { rule: rule ?? null, rule_facts };
      }
      setError('Expected an object with "rule" and/or "rule_facts" keys.');
      return null;
    } catch (e: any) {
      setError(`Invalid JSON: ${e?.message || 'parse error'}`);
      return null;
    }
  };

  const onBlur = () => {
    const parsed = validate();
    if (parsed) onApply(parsed);
  };

  return (
    <div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={onBlur}
        rows={8}
        spellCheck={false}
        className="mt-1 block w-full resize-y rounded-lg border border-gray-300 bg-gray-900 px-3 py-2 font-mono text-xs text-green-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600"
      />
      {error && (
        <p role="alert" className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3 w-3 shrink-0" /> {error}
        </p>
      )}
      <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
        Edits apply when you click away (validated as JSON only — shape mistakes still show up as
        a save error from the server, or a null rule on next load).
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public editor — the five compiled fields for one DRAFT criterion.
// ---------------------------------------------------------------------------

export interface CompiledRuleFields {
  rule: CriterionRule | null;
  rule_facts: CriterionRuleFact[];
  applies_to: 'any' | 'female' | 'male';
  qualifiers: string[];
  tier_hint: 'auto' | 'deterministic' | 'llm';
}

export function compiledFieldsFrom(c: Partial<StudyReviewCriterion>): CompiledRuleFields {
  return {
    rule: c.rule ?? null,
    rule_facts: Array.isArray(c.rule_facts) ? c.rule_facts : [],
    applies_to: c.applies_to || 'any',
    qualifiers: Array.isArray(c.qualifiers) ? c.qualifiers : [],
    tier_hint: c.tier_hint || 'auto',
  };
}

const APPLIES_TO_OPTIONS: { value: CompiledRuleFields['applies_to']; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'female', label: 'Female only' },
  { value: 'male', label: 'Male only' },
];

const TIER_HINT_HELP: Record<CompiledRuleFields['tier_hint'], string> = {
  auto: 'Tries a deterministic (code-only) resolution first when a rule or sex-scoping is present; falls back to AI when it can’t.',
  deterministic: 'Always attempts a deterministic resolution — escalates to AI normally if the facts can’t decide it.',
  llm: 'Skips the deterministic step entirely — always an AI judgment call (use for criteria a rule can’t safely express).',
};

export default function CriterionRuleEditor({ fields, onChange }: {
  fields: CompiledRuleFields;
  onChange: (fields: CompiledRuleFields) => void;
}) {
  const [jsonMode, setJsonMode] = useState(false);
  const factOptions = fields.rule_facts.map((f) => f.key).filter(Boolean);

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3 dark:border-gray-700 dark:bg-gray-900/20">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Facts-engine compiled rule
        </h4>
        <button
          type="button"
          onClick={() => setJsonMode((v) => !v)}
          className="text-xs font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
        >
          {jsonMode ? 'Use the visual builder' : 'Edit as JSON'}
        </button>
      </div>

      {/* applies_to — 3-option segmented control */}
      <div>
        <span className={labelCls}>Applies to</span>
        <div className="mt-1 inline-flex rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-600 dark:bg-gray-800">
          {APPLIES_TO_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange({ ...fields, applies_to: o.value })}
              aria-pressed={fields.applies_to === o.value}
              className={`min-h-[36px] rounded-md px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
                fields.applies_to === o.value
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
          Resolved from the patient&rsquo;s recorded sex; the other sex is automatically &ldquo;not
          applicable&rdquo; (never blocks). Unknown sex escalates to a human/AI look — never assumed.
        </p>
      </div>

      {/* tier_hint */}
      <div>
        <span className={labelCls}>Resolution</span>
        <select
          value={fields.tier_hint}
          onChange={(e) => onChange({ ...fields, tier_hint: e.target.value as CompiledRuleFields['tier_hint'] })}
          className={`${inputCls} mt-1`}
        >
          <option value="auto">Auto</option>
          <option value="deterministic">Deterministic</option>
          <option value="llm">AI</option>
        </select>
        <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{TIER_HINT_HELP[fields.tier_hint]}</p>
      </div>

      {/* qualifiers */}
      <div>
        <span className={labelCls}>Qualifiers</span>
        <div className="mt-1">
          <QualifiersInput qualifiers={fields.qualifiers} onChange={(qualifiers) => onChange({ ...fields, qualifiers })} />
        </div>
      </div>

      {/* rule + rule_facts */}
      <div>
        <span className={labelCls}>Rule</span>
        {jsonMode ? (
          <RawJsonEditor
            value={{ rule: fields.rule, rule_facts: fields.rule_facts }}
            onApply={({ rule, rule_facts }) => onChange({ ...fields, rule, rule_facts })}
          />
        ) : (
          <div className="mt-1 space-y-2">
            <StructuredRuleBuilder
              rule={fields.rule}
              factOptions={factOptions}
              onChange={(rule) => onChange({ ...fields, rule })}
            />
            {fields.rule && (
              <RuleFactsEditor
                facts={fields.rule_facts}
                onChange={(rule_facts) => onChange({ ...fields, rule_facts })}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only compact summary — confirmed sets (immutable) show this instead
// of the editor. e.g. "bmi 30–45 · vitals 180d", "Female only",
// "condition.thyroid_cancer status in active, historical, resolved".
// ---------------------------------------------------------------------------

function factWindowLabel(f: CriterionRuleFact): string {
  if (f.window_days != null) return `${f.window_days}d`;
  return DEFAULT_WINDOW_HINT[f.fact_type] ? DEFAULT_WINDOW_HINT[f.fact_type]!.replace('default ', '') : 'no window';
}

/** One leaf -> "key op value" (best-effort — never throws on odd shapes). */
function summarizeLeaf(leaf: CriterionRuleLeaf, rule_facts: CriterionRuleFact[]): string {
  const fact = leaf.fact
    ? rule_facts.find((f) => f.key === leaf.fact)
    : rule_facts[0];
  const key = fact?.key || leaf.fact || '(fact)';
  const opLabel = OP_LABEL.get(leaf.op) || leaf.op;
  if (leaf.op === 'between') return `${key} ${leaf.min ?? '?'}–${leaf.max ?? '?'}`;
  if (leaf.op === 'in') return `${key} ${opLabel} ${(leaf.values || []).join('/')}`;
  if (leaf.op === 'exists') return `${key} recorded`;
  return `${key} ${opLabel} ${leaf.value ?? '?'}`;
}

function summarizeRule(rule: CriterionRule | null | undefined, rule_facts: CriterionRuleFact[]): string | null {
  if (!rule) return null;
  if (isLeaf(rule)) return summarizeLeaf(rule, rule_facts);
  if (isGroup(rule)) {
    const parts = rule.conditions.map((c) => summarizeRule(c, rule_facts) || '…');
    return parts.length > 1 ? `(${parts.join(` ${rule.combinator.toUpperCase()} `)})` : parts[0] || null;
  }
  return null;
}

export function CriterionRuleSummary({ criterion }: { criterion: StudyReviewCriterion }) {
  const fields = compiledFieldsFrom(criterion);
  const chips: string[] = [];

  const ruleSummary = summarizeRule(fields.rule, fields.rule_facts);
  if (ruleSummary) chips.push(ruleSummary);

  // Recency windows worth surfacing (dedup by window label).
  const windows = Array.from(new Set(
    fields.rule_facts
      .filter((f) => f.fact_type === 'lab_result' || f.fact_type === 'vital')
      .map((f) => `${f.fact_type === 'lab_result' ? 'labs' : 'vitals'} ${factWindowLabel(f)}`)));
  chips.push(...windows);

  if (fields.applies_to !== 'any') {
    chips.push(fields.applies_to === 'female' ? 'Female only' : 'Male only');
  }
  if (fields.qualifiers.length > 0) chips.push(`qualifiers: ${fields.qualifiers.join(', ')}`);
  if (fields.tier_hint !== 'auto') chips.push(fields.tier_hint === 'llm' ? 'AI-only' : 'deterministic-forced');

  if (chips.length === 0) {
    return (
      <span className="text-xs italic text-gray-400 dark:text-gray-500">
        No compiled rule — AI judgment call.
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {chips.map((c, i) => (
        <span
          key={i}
          className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300"
        >
          {c}
        </span>
      ))}
    </span>
  );
}

/** Human label for fact_type, exported for reuse by callers rendering facts. */
export { factTypeLabel };
